/**
 * Dialog primitive.
 *
 * A centred, modal overlay wrapped over `@radix-ui/react-dialog`. Radix
 * owns the headless behaviour — portal mounting, focus trap, escape-key
 * dismissal, overlay click dismissal, ARIA role/title/description
 * association. Our job is to pin token-backed styling onto Radix's
 * primitives without adding our own behaviour on top.
 *
 * Compound-component shape (inherited by T007 — Tabs follows the
 * same convention): each subcomponent is a **dedicated named export**
 * (`Root`, `Trigger`, `Content`, `Title`, `Description`, `Close`) AND the
 * module also exposes an aggregate `Dialog` object via
 * `export const Dialog = { Root, Trigger, Content, Title, Description, Close }`.
 * Callers prefer the aggregate
 * (`<Dialog.Root><Dialog.Trigger>…</Dialog.Trigger></Dialog.Root>`) for
 * readability; the dedicated named exports exist so bundlers can tree-shake
 * at the subcomponent level and so tests can import narrowly
 * (`import { Content } from '../Dialog.js'`). `Content` embeds the Radix
 * `Portal` + `Overlay` internally so consumers do not get a public
 * double-portal composition trap; if a user story later needs custom
 * Portal / Overlay placement, this file extends the API deliberately
 * rather than pre-exposing it here. `Root`, `Trigger`, and `Close` pass
 * Radix's subcomponent through unstyled; `Title`, `Description`, and the
 * styled `Content` / `Close` inject token-backed classes before
 * forwarding remaining props. `Close` is wrapped so its focus-visible
 * ring matches Button's sanctioned treatment (FR-033).
 *
 * Styling is token-backed only: overlay uses `bg-foreground/70` for a
 * dim backdrop (the `/70` opacity modifier resolves against the
 * `--color-foreground` token, not a raw hex); content uses `bg-popover`
 * (`--color-popover` — the dedicated overlay-surface token), `text-popover-foreground`,
 * `border-border`, `rounded-lg`, and `shadow-xl` (Tailwind v4's default
 * `shadow-*` utilities ship out of the box — no arbitrary-bracket class).
 * Size defaults to `max-w-md` (28rem, a comfortable mid-modal width);
 * callers that need a wider surface pass `className="max-w-lg"` or
 * similar and the caller override wins (see `cn(...)` ordering in
 * `./utils.js`). Every class below is a token-backed Tailwind utility —
 * no arbitrary-bracket classes (FR-047), no raw colour literals (FR-002).
 *
 * Animation: Radix exposes `data-[state=open|closed]` attributes on
 * Content and Overlay. Cluster 1 keeps animations minimal — a simple
 * `transition-opacity duration-200 ease-out` on the overlay is the only
 * transition we apply; content does not animate. Richer motion
 * (scale-in, fade-in combinations) is out of scope here and can be added
 * at the point a user story demands it. `prefers-reduced-motion` is
 * honoured automatically by token-backed transitions because Tailwind
 * v4 emits `@media (prefers-reduced-motion: reduce)` overrides by default
 * for its transition utilities.
 */

import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { ComponentProps } from 'react';

import { cn } from './utils.js';

/**
 * `Dialog.Title` font scale. `default` is the historical heading; `hero` is the
 * larger proto-aligned takeover heading. A typed prop (not a caller `className`
 * override) because `cn()` is plain concatenation with no `tailwind-merge`, so a
 * caller `text-3xl` cannot reliably defeat the baked-in `text-lg`; exactly one
 * font-size class must be emitted (mirrors the Button `shape`/`size` rationale).
 */
export type DialogTitleSize = 'default' | 'hero' | 'wizard' | 'call';

export type DialogTitleProps = ComponentProps<typeof DialogPrimitive.Title> & {
  readonly size?: DialogTitleSize;
};
export type DialogDescriptionProps = ComponentProps<typeof DialogPrimitive.Description>;
export type DialogCloseProps = ComponentProps<typeof DialogPrimitive.Close>;

/**
 * `Dialog.Content` layout variant.
 *
 * - `centered` (default): the historical behaviour — fixed-position card at
 *   the viewport centre, `max-w-md`, rounded + shadowed, with the dim
 *   overlay behind. Every existing consumer (e.g. `FeedbackModal`)
 *   continues to render exactly as before because this is the default.
 * - `bottom`: an optional, bottom-positioned modal panel. It is a rounded-top
 *   mobile sheet and a moderately wide bottom-centred card on desktop. The
 *   associated backdrop is deliberately lighter than a blocking confirmation
 *   dialog, but focus and dismissal semantics remain those of Radix Dialog.
 * - `fullscreen-mobile`: introduced for the 016 Onboarding Wizard. On mobile
 *   viewports the content fills the entire screen (`fixed inset-0`, no
 *   max-width/centring/rounding/shadow); at `md:` and above it switches to
 *   a centred `max-w-lg` card with `max-h-dialog-max` (= 85 dvh via
 *   `--spacing-dialog-max`), rounded + shadowed. Spec refs: FR-012, FR-017.
 * - `takeover`: introduced for the 026 contact-update surface (the proto's
 *   `UpdateContactsModal`). Fills the full viewport at EVERY breakpoint — no
 *   `md:` centring, no card chrome. Uses the app surface token (`bg-background`
 *   / `text-foreground`) rather than the overlay-surface `bg-popover`, because
 *   a takeover replaces the page rather than floating above it. `overflow-y-auto`
 *   keeps tall content scrollable; the consumer lays out its own centred
 *   `max-w-md` column + sticky action bar inside `Content`.
 * - `wizard`: introduced for the 027 compose wizard (the proto's guided-wizard
 *   modal). Fills the viewport on mobile; at `md:` a centred FIXED-height card
 *   (85dvh limited to the visible viewport, capped at `max-h-wizard-dialog-max` = 800px,
 *   `w-wizard-dialog-w` = 600px). A `flex flex-col` with NO overflow of its
 *   own: the consumer pins its top bar and bottom bar as static flex children
 *   and owns scrolling in an internal `flex-1 min-h-0 overflow-y-auto` step
 *   area — that inner area (not the dialog) is why step content can never
 *   hide behind, or overlap, the pinned chrome. A typed variant rather than a
 *   caller `className`: `cn()` is plain concatenation, so a competing
 *   same-property class resolves by Tailwind compile order, not by intent.
 */
export type DialogContentVariant =
  | 'centered'
  | 'bottom'
  | 'fullscreen-mobile'
  | 'takeover'
  | 'wizard';

export type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> & {
  readonly variant?: DialogContentVariant;
};

const overlayClasses =
  'fixed inset-0 z-40 bg-foreground/70 transition-opacity duration-200 ease-out';

const bottomOverlayClasses =
  'fixed inset-0 z-40 bg-foreground/25 transition-opacity duration-200 ease-out';

const centeredContentClasses =
  'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 ' +
  'bg-popover text-popover-foreground border border-border rounded-lg shadow-xl ' +
  'p-4 ' +
  'focus-visible:outline-none';

// Optional-notice surface: a bottom sheet on mobile, changing into a
// moderately wide card above the bottom edge on desktop. The content itself
// scrolls on short screens so both equally-weighted choices remain reachable.
const bottomContentClasses =
  'fixed bottom-0 left-0 z-50 w-full max-h-dialog-max overflow-y-auto ' +
  'bg-popover text-popover-foreground border border-border border-b-0 rounded-t-3xl shadow-xl p-5 ' +
  'focus-visible:outline-none ' +
  'md:bottom-6 md:left-1/2 md:w-full md:max-w-lg md:-translate-x-1/2 md:rounded-lg md:border-b';

// Mobile (< 768 px): fills the viewport — no centring translate, no max-width,
// no rounding/border/shadow. Desktop (≥ 768 px via `md:`): centred card at
// max-w-lg, max-h-dialog-max (token = 85 dvh), rounded + shadowed.
// `md:max-h-dialog-max` references the `--spacing-dialog-max` token in
// `tokens/tokens.css` — NOT an arbitrary-value bracket class.
//
// `overflow-y-auto` is load-bearing: on viewports smaller than the wizard
// content (e.g. 320×480), the variant otherwise traps tall content behind
// `fixed inset-0` with no scroll affordance (spec Edge Cases: "Content
// should remain readable and scrollable at any supported viewport width").
// On desktop it also handles the case where step content exceeds the
// `max-h-dialog-max` card height.
const fullscreenMobileContentClasses =
  'fixed inset-0 z-50 overflow-y-auto bg-popover text-popover-foreground p-4 focus-visible:outline-none ' +
  'md:left-1/2 md:top-1/2 md:inset-auto md:-translate-x-1/2 md:-translate-y-1/2 ' +
  'md:w-full md:max-w-lg md:max-h-dialog-max md:border md:border-border md:rounded-lg md:shadow-xl';

// Full-viewport at ALL breakpoints (no `md:` centring) — the contact-update
// takeover surface (026, the proto's `UpdateContactsModal`). `bg-background`
// is the app surface token (not the overlay-surface `bg-popover`) so the
// takeover reads as a replacement page rather than a floating card.
// `overflow-y-auto` is load-bearing: the surface owns no card height, so tall
// content (the full address/contact form) scrolls within the viewport.
const takeoverContentClasses =
  'fixed inset-0 z-50 overflow-y-auto bg-background text-foreground focus-visible:outline-none';

// Guided-wizard shell (027 compose wizard): viewport-filling flex column on
// mobile; at `md:` a centred card — 85dvh, limited to the visible viewport and capped
// at `max-h-wizard-dialog-max` (800px), `w-wizard-dialog-w` (600px); all three
// are `--spacing-*` tokens, no bracket classes. Deliberately NO overflow here:
// the consumer's inner `flex-1 min-h-0 overflow-y-auto` step area owns
// scrolling, keeping the top bar and bottom action bar pinned (the
// content-sized desktop card let the calendar popover clip at the dialog edge
// and overlap the pinned continue button — the owner-reported defect).
const wizardContentClasses =
  'fixed inset-x-0 top-wizard-viewport-top z-50 flex h-wizard-viewport flex-col ' +
  'bg-popover text-popover-foreground focus-visible:outline-none ' +
  'md:left-1/2 md:top-wizard-viewport-center md:inset-auto md:-translate-x-1/2 md:-translate-y-1/2 ' +
  'md:h-wizard-viewport-card md:max-h-wizard-dialog-max md:w-wizard-dialog-w ' +
  'md:border md:border-border md:rounded-lg md:shadow-xl';

const titleBaseClasses = 'text-foreground';

const titleSizeClasses = {
  default: 'text-lg font-semibold',
  hero: 'text-3xl font-bold',
  wizard: 'text-2xl font-bold md:text-3xl',
  call: 'text-xl font-bold sm:text-2xl',
} as const satisfies Record<DialogTitleSize, string>;

const descriptionClasses = 'text-sm text-muted-foreground';

const closeClasses =
  'inline-flex items-center justify-center rounded-md ' +
  'transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

export const Root = DialogPrimitive.Root;
export const Trigger = DialogPrimitive.Trigger;

export function Content({
  className,
  children,
  variant = 'centered',
  ...rest
}: DialogContentProps) {
  const variantClasses =
    variant === 'bottom'
      ? bottomContentClasses
      : variant === 'fullscreen-mobile'
        ? fullscreenMobileContentClasses
        : variant === 'takeover'
          ? takeoverContentClasses
          : variant === 'wizard'
            ? wizardContentClasses
            : centeredContentClasses;
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={variant === 'bottom' ? bottomOverlayClasses : overlayClasses}
      />
      <DialogPrimitive.Content className={cn(variantClasses, className)} {...rest}>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function Title({ className, size = 'default', ...rest }: DialogTitleProps) {
  return (
    <DialogPrimitive.Title
      className={cn(titleBaseClasses, titleSizeClasses[size], className)}
      {...rest}
    />
  );
}

export function Description({ className, ...rest }: DialogDescriptionProps) {
  return <DialogPrimitive.Description className={cn(descriptionClasses, className)} {...rest} />;
}

export function Close({ className, ...rest }: DialogCloseProps) {
  return <DialogPrimitive.Close className={cn(closeClasses, className)} {...rest} />;
}

export const Dialog = {
  Root,
  Trigger,
  Content,
  Title,
  Description,
  Close,
} as const;
